import { type DynamicDataContext, type DynamicDataObservable, useDynamicData } from '@avensia/excite';
import * as React from 'react';

type NameToPropertyType<T> = { name: string; property: keyof T };
export function getFormData<T>(form: HTMLFormElement, mapInputNameToProperty?: NameToPropertyType<T>[]) {
  const object: any = {};
  const formData = new FormData(form);
  formData.forEach(function (value, key) {
    const newKey = mapInputNameToProperty?.find(p => p.name === key)?.property || key;
    object[newKey] = value;
  });
  return object as T;
}

export function useDynamicDataField<TData extends Excite.ContentApi.IDynamicData>(
  options: {
    elementRef: React.RefObject<HTMLInputElement | null>;
    selector: (data?: TData) => boolean | undefined;
    context?: DynamicDataContext;
  },
  type?: string,
): [checked: boolean | undefined, dynamicData: DynamicDataObservable<TData>];

export function useDynamicDataField<TData extends Excite.ContentApi.IDynamicData>(
  options: {
    elementRef:
      | React.RefObject<HTMLInputElement | null>
      | React.RefObject<HTMLTextAreaElement | null>
      | React.RefObject<HTMLSelectElement | null>;
    selector: (data?: TData) => string | undefined;
    context?: DynamicDataContext;
  },
  type?: string,
): [value: string | undefined, dynamicData: DynamicDataObservable<TData>];

export function useDynamicDataField<TData extends Excite.ContentApi.IDynamicData>(
  options: {
    elementRef:
      | React.RefObject<HTMLInputElement | null>
      | React.RefObject<HTMLTextAreaElement | null>
      | React.RefObject<HTMLSelectElement | null>;
    selector: (data?: TData) => string | boolean | undefined;
    context?: DynamicDataContext | {};
  },
  type?: string,
) {
  const { elementRef, selector, context } = options;
  const [value, dynamicData] = useDynamicData<TData, string | boolean | undefined>({ selector, context }, type);

  React.useEffect(() => {
    const element = elementRef.current;
    if (element) {
      if (typeof value === 'string' && document.activeElement !== element && element.value !== value) {
        element.value = value;
      } else if (typeof value === 'boolean' && element instanceof HTMLInputElement && element.checked !== value) {
        element.checked = value;
      }
    }
  }, [elementRef, value]);

  return [value, dynamicData] as const;
}

export function useDynamicDataUpdateField<TData extends Excite.ContentApi.IDynamicData>(
  options: {
    inputRef:
      | React.RefObject<HTMLInputElement | null>
      | React.RefObject<HTMLTextAreaElement | null>
      | React.RefObject<HTMLSelectElement | null>;
    selector: (data?: TData) => string | undefined;
    update: (value: string) => void;
    updateOnInvalid?: React.RefObject<boolean | null>;
    context?: DynamicDataContext | {};
  },
  type?: string,
) {
  console.log('type', type);
  const { inputRef, selector, update, updateOnInvalid, context } = options;
  const [value, dynamicData] = useDynamicDataField<TData>(
    {
      elementRef: inputRef,
      selector,
      context: context || {},
    },
    type,
  );

  const refs = React.useRef({ selector, update });
  const previousValue = React.useRef<string>(undefined);
  refs.current = { selector, update };

  const updateIfNeeded = React.useCallback(
    async (value: string) => {
      if (previousValue.current !== value && (inputRef.current?.validity.valid || updateOnInvalid?.current)) {
        refs.current.update(value);
        previousValue.current = value;
      }
    },
    [dynamicData, inputRef, updateOnInvalid],
  );

  React.useEffect(() => {
    const input = inputRef.current;
    const form = input?.form;

    // Triggers update on submit
    function submit() {
      if (input) {
        updateIfNeeded(input.value);
      }
    }

    // Triggers update on change when not in focus (e.g. autofill)
    function change() {
      if (input && document.activeElement !== input) {
        updateIfNeeded(input.value);
      }
    }

    if (form) {
      form.addEventListener('submit', submit);
    }

    if (input) {
      input.addEventListener('change', change);
    }

    return () => {
      if (form) {
        form.removeEventListener('submit', submit);
      }

      if (input) {
        input.removeEventListener('change', change);
      }
    };
  }, [inputRef, updateIfNeeded]);

  const onBlur = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => updateIfNeeded(e.currentTarget.value),
    [updateIfNeeded],
  );

  return React.useMemo(() => ({ defaultValue: value, onBlur }), [onBlur, value]);
}
